import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import { requestTypeLabelMap } from '~/common/dataRequestUtils';
import type { CivilGridFile } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import Icon from '~/components2/Icon';
import type { UtilityAgency } from '../../common';
import { downloadAndTrackUtilitiesFile } from '../../common/trackUtilitiesFileDownload';
import store from '../../store';
import { requestMatchesRow } from '../grouping';

interface Props {
  agency: UtilityAgency;
}

export default function DocumentsCell({ agency }: Props) {
  const { dataRequests, project } = useSnapshot(store) as typeof store;

  const requests = Object.values(dataRequests).filter((d) => requestMatchesRow(d, agency));

  const filesAvailable = requests.some((r) => r.files.length > 0);

  const onFileDownload = (file: CivilGridFile) => downloadAndTrackUtilitiesFile(file, 'dataRequest', project?.id);

  if (filesAvailable) {
    const rows: ReactNode[] = [];

    requests.forEach((r) => {
      if (r.files.length > 0) {
        const fileList = r.files.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onFileDownload(f)}
            className="cursor-pointer break-words text-left text-xs text-blue-50 underline hover:text-blue-60"
            data-testid={`utilities-file-download-${f.id}`}
          >
            {f.name}
          </button>
        ));

        rows.push(
          <div key={r.id}>
            <div className="font-semibold text-sm">{requestTypeLabelMap[r.type]}</div>
            <div className="flex flex-col text-xs">{fileList}</div>
          </div>,
        );
      }
    });

    return (
      <HoverCard
        trigger={
          <span className="inline-block py-1">
            <Icon icon="file" size={18} />
          </span>
        }
      >
        <div className="space-y-4">{rows}</div>
      </HoverCard>
    );
  }
  return null;
}
